import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link, useLocation } from 'react-router-dom';
import { leadService, leadActivityService, userService, brandService, companyService, getWorkspaceId } from '../services/api';
import { INDIA_STATES_CITIES } from '../data/indiaCities';
import * as XLSX from 'xlsx';
import { EyeIcon, EyeSlashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { LeadActivityPanel } from '../components/LeadActivityPanel';
import BulkUploadModal from '../components/BulkUploadModal';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { todayIST, yesterdayIST, istDay, daysBetween, formatDay, formatIST } from '../utils/dates';
import { qualificationOf } from '../utils/qualification';
import { Spinner } from '../components/Spinner';
import { CrmSyncBadge, useCrmSyncStatus, canSeeCrmSync } from '../components/CrmSyncBadge';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  ArrowDownTrayIcon,
  BuildingOfficeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentArrowUpIcon,
  UserPlusIcon,
  TrashIcon,
  ArrowRightIcon,
  ArrowsRightLeftIcon,
} from '@heroicons/react/24/outline';

// ── Constants ────────────────────────────────────────────────────

const STATUS_CONFIG = {
  new:         { label: 'New',         color: 'bg-blue-100 text-blue-800' },
  in_progress: { label: 'In Progress', color: 'bg-indigo-100 text-indigo-800' },
  follow_up:   { label: 'Follow Up',   color: 'bg-orange-100 text-orange-800' },
  converted:   { label: 'Converted',   color: 'bg-green-100 text-green-800' },
  lost:        { label: 'Lost',        color: 'bg-red-100 text-red-800' },
};

const PRIORITY_CONFIG = {
  low:    { label: 'Low',    color: 'bg-gray-100 text-gray-600' },
  medium: { label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
  high:   { label: 'High',   color: 'bg-orange-100 text-orange-800' },
  urgent: { label: 'Urgent', color: 'bg-red-100 text-red-800' },
};

const BUYING_TIME_LABELS = {
  immediate:       'Immediate',
  within_1_month:  'Within 1 Month',
  within_3_months: 'Within 3 Months',
  within_6_months: 'Within 6 Months',
  just_enquiring:  'Just Enquiring',
  other:           'Other',
};

// Buying Time label — shows the customer's original custom answer (e.g. an
// unrecognized Facebook lead-form response) instead of the generic "Other".
function buyingTimeLabel(leadInfo) {
  if (leadInfo?.buyingTime === 'other' && leadInfo?.buyingTimeRaw) return leadInfo.buyingTimeRaw;
  return BUYING_TIME_LABELS[leadInfo?.buyingTime] || '—';
}

// Older Facebook-imported leads may have underscores baked into free-text
// values (e.g. "Maruti_Suzuki") from before this was cleaned on ingestion.
function cleanDisplay(text) {
  return (text || '').replace(/_/g, ' ').trim();
}

// Urgency badge shown in "Buying in Days" column
const BUYING_URGENCY = {
  immediate:      { label: '🔥 Hot',  cls: 'bg-orange-100 text-orange-700 border border-orange-200' },
  within_1_month: { label: '⚡ Warm', cls: 'bg-yellow-100 text-yellow-700 border border-yellow-200' },
};

const COL_COUNT = 10;

const SOURCE_LABELS = {
  website:      'Website',
  phone:        'Phone',
  email:        'Email',
  social_media: 'Social Media',
  facebook:     'DOW',
  referral:     'Referral',
  walk_in:      'Walk-in',
  other:        'Other',
};

// ── Small helpers ────────────────────────────────────────────────

function InfoRow({ label, value }) {
  return (
    <div className="flex gap-2 py-0.5 min-w-0">
      <span className="text-gray-400 w-32 flex-shrink-0 text-sm">{label}</span>
      <span className="text-gray-800 font-medium text-sm truncate min-w-0" title={value || '—'}>{value || '—'}</span>
    </div>
  );
}

function InfoCol({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="text-sm text-gray-400 mb-0.5">{label}</div>
      <div className="text-[15px] font-medium text-gray-800 truncate" title={value || '—'}>{value || '—'}</div>
    </div>
  );
}

function SectionCard({ title, badge, children }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <div className="mb-4 pb-2 border-b border-gray-100 flex items-center gap-2">
        <span className="inline-block px-3 py-1 rounded text-xs font-bold text-white bg-[#f75d34] uppercase tracking-wide">
          {title}
        </span>
        {badge != null && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            {badge}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

// ── BulkAssignModal ────────────────────────────────────────────────
function BulkAssignModal({ selectedCount, allUsers, user, onClose, onAssign, loading, selectedLeadBrands = [] }) {
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [remark, setRemark]               = useState('');
  const [search, setSearch]               = useState('');
  const [showRole,     setShowRole]       = useState('');  // which role to list
  const [filterAdmin,  setFilterAdmin]    = useState('');
  const [filterDealer, setFilterDealer]   = useState('');

  const ROLE_RANK   = { super_admin: 4, admin: 3, dealer: 2, sales_executive: 1 };
  const ROLE_LABELS = { super_admin: 'Super Admin', admin: 'Admin', dealer: 'Dealer', sales_executive: 'Sales Executive' };
  const myRank      = ROLE_RANK[user?.role] || 0;
  const myRole      = user?.role;

  const userMap = useMemo(() => {
    const m = {};
    allUsers.forEach(u => { m[String(u._id)] = u; });
    return m;
  }, [allUsers]);

  const getParentId = (u) => {
    const pid = typeof u.parentId === 'object' ? u.parentId?._id : u.parentId;
    return pid ? String(pid) : null;
  };

  const getAncestors = (u) => {
    const chain = [];
    let current = u;
    for (let i = 0; i < 5; i++) {
      const pid = getParentId(current);
      if (!pid) break;
      const parent = userMap[pid];
      if (!parent) break;
      // Skip super_admin — it's implicit at the top of the tree, no need to show
      if (parent.role !== 'super_admin') chain.push(parent);
      current = parent;
    }
    return chain;
  };

  const assignableRoles = Object.entries(ROLE_RANK)
    .filter(([, rank]) => rank < myRank)
    .map(([role]) => role);

  // Brand-match: dealer carries any of the selected leads' brands (union).
  // If no leads / no brands → no filter.
  const dealerCarriesBrand = (u) => {
    if (u.role !== 'dealer') return true;
    if (selectedLeadBrands.length === 0) return true;
    const names = (u.brands || []).map(b => (b?.name || '').toLowerCase());
    return selectedLeadBrands.some(wanted => wanted && names.includes(wanted));
  };

  // Cascading lists
  const adminList = useMemo(() =>
    allUsers.filter(u => u.role === 'admin'),
  [allUsers]);

  const dealerList = useMemo(() => {
    let dealers = allUsers.filter(u => u.role === 'dealer');
    if (filterAdmin) dealers = dealers.filter(u => getParentId(u) === filterAdmin);
    return dealers.filter(dealerCarriesBrand);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allUsers, filterAdmin, selectedLeadBrands]);

  const filteredUsers = useMemo(() => allUsers.filter(u => {
    if (!assignableRoles.includes(u.role)) return false;
    // Brand-match: hide dealers (and sales execs whose dealer doesn't match)
    if (u.role === 'dealer' && !dealerCarriesBrand(u)) return false;
    if (u.role === 'sales_executive') {
      const parentDealer = userMap[getParentId(u)];
      if (parentDealer && parentDealer.role === 'dealer' && !dealerCarriesBrand(parentDealer)) return false;
    }
    // Role filter
    if (showRole && u.role !== showRole) return false;
    // Cascade: dealer filter → only sales_execs under that dealer
    if (filterDealer) {
      return u.role === 'sales_executive' && getParentId(u) === filterDealer;
    }
    // Admin filter → only dealers/sales_execs under that admin's tree
    if (filterAdmin) {
      if (u.role === 'admin') return String(u._id) === filterAdmin;
      if (u.role === 'dealer') return getParentId(u) === filterAdmin;
      if (u.role === 'sales_executive') {
        const dealer = userMap[getParentId(u)];
        return dealer && getParentId(dealer) === filterAdmin;
      }
      return false;
    }
    if (search) {
      const q = search.toLowerCase();
      const ancestors = getAncestors(u).map(a => `${a.firstName} ${a.lastName}`).join(' ');
      return `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) || ancestors.toLowerCase().includes(q);
    }
    return true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [allUsers, showRole, filterAdmin, filterDealer, search, assignableRoles, userMap, selectedLeadBrands]);

  const toggleUser = (id) => setSelectedUsers(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleAll = () => {
    if (selectedUsers.size === filteredUsers.length && filteredUsers.every(u => selectedUsers.has(u._id))) {
      setSelectedUsers(prev => {
        const next = new Set(prev);
        filteredUsers.forEach(u => next.delete(u._id));
        return next;
      });
    } else {
      setSelectedUsers(prev => {
        const next = new Set(prev);
        filteredUsers.forEach(u => next.add(u._id));
        return next;
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-3 py-4">
        <div className="fixed inset-0 bg-black bg-opacity-40" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[92vh] flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Assign Leads</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {selectedCount} lead{selectedCount > 1 ? 's' : ''} →{' '}
                {selectedUsers.size > 0
                  ? <span className="text-primary-600 font-medium">{selectedUsers.size} user{selectedUsers.size > 1 ? 's' : ''} selected</span>
                  : 'select users below'}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="p-5 space-y-3 overflow-y-auto flex-1">
            {/* Filters */}
            <div className="space-y-2">
              {/* Show role */}
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500 w-14 flex-shrink-0">Show</label>
                <select value={showRole} onChange={e => { setShowRole(e.target.value); setFilterAdmin(''); setFilterDealer(''); }}
                  className="input text-sm flex-1">
                  <option value="">All</option>
                  {assignableRoles.map(r => (
                    <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
                  ))}
                </select>
              </div>

              {/* Admin cascade — only when showing dealers or sales execs */}
              {myRole === 'super_admin' && (!showRole || showRole === 'dealer' || showRole === 'sales_executive') && adminList.length > 0 && (
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500 w-14 flex-shrink-0">Admin</label>
                  <select value={filterAdmin} onChange={e => { setFilterAdmin(e.target.value); setFilterDealer(''); }}
                    className="input text-sm flex-1">
                    <option value="">All Admins</option>
                    {adminList.map(u => (
                      <option key={u._id} value={u._id}>{u.firstName} {u.lastName}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Dealer cascade — only when showing sales execs */}
              {(myRole === 'super_admin' || myRole === 'admin') && (!showRole || showRole === 'sales_executive') && dealerList.length > 0 && (
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500 w-14 flex-shrink-0">Dealer</label>
                  <select value={filterDealer} onChange={e => setFilterDealer(e.target.value)}
                    className="input text-sm flex-1">
                    <option value="">All Dealers</option>
                    {dealerList.map(u => (
                      <option key={u._id} value={u._id}>{u.firstName} {u.lastName}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Search */}
              <input
                type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search by name..." className="input text-sm w-full"
              />
            </div>

            {/* User checkbox list */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Select all row */}
              {filteredUsers.length > 0 && (
                <div
                  className="flex items-center gap-3 px-3 py-2 bg-gray-50 border-b border-gray-200 cursor-pointer hover:bg-gray-100"
                  onClick={toggleAll}
                >
                  <input
                    type="checkbox" readOnly
                    checked={filteredUsers.length > 0 && selectedUsers.size === filteredUsers.length}
                    ref={el => { if (el) el.indeterminate = selectedUsers.size > 0 && selectedUsers.size < filteredUsers.length; }}
                    className="rounded accent-primary-600"
                  />
                  <span className="text-xs font-semibold text-gray-600">Select All ({filteredUsers.length})</span>
                </div>
              )}

              {/* User rows */}
              <div className="max-h-52 overflow-y-auto divide-y divide-gray-100">
                {filteredUsers.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-4">No users available</p>
                ) : filteredUsers.map(u => {
                  const ancestors = getAncestors(u);
                  return (
                    <div
                      key={u._id}
                      className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${selectedUsers.has(u._id) ? 'bg-primary-50' : 'hover:bg-gray-50'}`}
                      onClick={() => toggleUser(u._id)}
                    >
                      <input
                        type="checkbox" readOnly checked={selectedUsers.has(u._id)}
                        className="rounded accent-primary-600 flex-shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-sm font-medium text-gray-800 truncate">
                            {u.firstName} {u.lastName}
                            {u.role === 'dealer' && Array.isArray(u.brands) && u.brands.length > 0 && (
                              <span className="text-gray-500 font-normal"> ({u.brands.map(b => b.name).filter(Boolean).join(', ')})</span>
                            )}
                          </p>
                          <span className="text-[11px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium flex-shrink-0">
                            {ROLE_LABELS[u.role] || u.role}
                          </span>
                        </div>
                        {ancestors.length > 0 && (
                          <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                            {ancestors.map((a, i) => (
                              <React.Fragment key={a._id}>
                                {i === 0 && <span className="text-[10px] text-gray-400">Under:</span>}
                                {i > 0 && <span className="text-[10px] text-gray-300">›</span>}
                                <span className="text-[11px] text-gray-600 font-medium">
                                  {a.firstName} {a.lastName}
                                  {a.role === 'dealer' && Array.isArray(a.brands) && a.brands.length > 0 && (
                                    <span className="text-gray-400 font-normal"> ({a.brands.map(b => b.name).filter(Boolean).join(', ')})</span>
                                  )}
                                </span>
                                <span className="text-[10px] px-1 py-0.5 rounded bg-gray-100 text-gray-500 font-medium">
                                  {ROLE_LABELS[a.role] || a.role}
                                </span>
                              </React.Fragment>
                            ))}
                          </div>
                        )}
                      </div>
                      {selectedUsers.has(u._id) && (
                        <span className="text-xs text-primary-600 font-medium flex-shrink-0">✓</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Selected users summary */}
            {selectedUsers.size > 0 && (
              <div className="rounded-lg border border-primary-200 bg-primary-50 px-3 py-2">
                <p className="text-xs font-semibold text-primary-700 mb-1.5">
                  Selected ({selectedUsers.size})
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {Array.from(selectedUsers).map(id => {
                    const u = userMap[id];
                    if (!u) return null;
                    return (
                      <span key={id} className="inline-flex items-center gap-1 text-xs bg-white border border-primary-200 text-primary-800 rounded-full px-2 py-0.5 font-medium">
                        {u.firstName} {u.lastName}
                        <span className="text-[10px] text-primary-500">({ROLE_LABELS[u.role] || u.role})</span>
                        <button
                          type="button"
                          onClick={e => { e.stopPropagation(); toggleUser(id); }}
                          className="ml-0.5 text-primary-400 hover:text-primary-700 leading-none"
                        >×</button>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Remark */}
            <div>
              <label className="block text-xs text-gray-500 mb-1">Remark</label>
              <textarea value={remark} onChange={e => setRemark(e.target.value)}
                className="input text-sm w-full resize-none" rows={2}
                placeholder="Optional remark..." />
            </div>
          </div>

          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
            <button onClick={onClose} className="btn-outline-secondary text-sm px-5">Cancel</button>
            <button
              onClick={() => onAssign({ assignedToList: Array.from(selectedUsers), remark })}
              disabled={selectedUsers.size === 0 || loading}
              className="btn-primary text-sm px-6 flex items-center gap-2"
            >
              {loading
                ? <><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />Assigning...</>
                : `Assign to ${selectedUsers.size || '...'} User${selectedUsers.size !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── BulkForwardModal ────────────────────────────────────────────────
// Forward N selected leads to a single target user in the next role-tier.
// Unlike Bulk Assign (which creates copies), this MOVES each lead.
// Company-only: copy/forward selected leads to another workspace (super admin + team)
function TransferWorkspaceModal({ selectedCount, onClose, onTransfer, loading }) {
  const [targetTenantId, setTargetTenantId] = useState('');
  const [mode, setMode] = useState('copy');

  const { data } = useQuery('companyWorkspaces', companyService.getWorkspaces);
  const currentId = getWorkspaceId();
  const targets = (data?.data?.data?.workspaces || []).filter(
    w => String(w._id) !== String(currentId) && w.status === 'active'
  );

  const submit = () => {
    if (!targetTenantId) { toast.error('Please choose a target workspace'); return; }
    onTransfer({ targetTenantId, mode });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Send {selectedCount} lead{selectedCount > 1 ? 's' : ''} to another workspace</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><XMarkIcon className="h-5 w-5" /></button>
        </div>

        <div className="px-5 py-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Target workspace (super admin & their team)</label>
            <select className="input" value={targetTenantId} onChange={e => setTargetTenantId(e.target.value)}>
              <option value="">— Select workspace —</option>
              {targets.map(w => <option key={w._id} value={w._id}>{w.name}</option>)}
            </select>
            {targets.length === 0 && <p className="mt-1 text-xs text-gray-400">No other active workspaces available.</p>}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Action</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode('copy')}
                className={`px-3 py-2 rounded-lg border text-sm font-medium ${mode === 'copy' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-600'}`}
              >
                Copy<span className="block text-xs font-normal text-gray-400">keep in this workspace</span>
              </button>
              <button
                type="button"
                onClick={() => setMode('forward')}
                className={`px-3 py-2 rounded-lg border text-sm font-medium ${mode === 'forward' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-600'}`}
              >
                Forward<span className="block text-xs font-normal text-gray-400">move with full history</span>
              </button>
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-3">
          <button onClick={onClose} className="btn-outline-secondary" disabled={loading}>Cancel</button>
          <button onClick={submit} className="btn-primary" disabled={loading || !targetTenantId}>
            {loading ? <><Spinner size="sm" className="mr-2" />Sending…</> : (mode === 'copy' ? 'Copy Leads' : 'Forward Leads')}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkForwardModal({ selectedCount, allUsers, user, onClose, onForward, loading }) {
  const [forwardToId, setForwardToId] = useState('');
  const [remark, setRemark]           = useState('');
  const [selectedAdminId, setSelectedAdminId] = useState(''); // super_admin → pick admin → then dealers

  const isSuperAdmin = user?.role === 'super_admin';
  const NEXT_ROLE = { super_admin: 'admin', admin: 'dealer', dealer: 'sales_executive' };
  const nextRole  = NEXT_ROLE[user?.role];
  const ROLE_LABELS = { admin: 'Admin', dealer: 'Dealer', sales_executive: 'Sales Executive' };

  const getParentId = (u) => {
    const pid = typeof u.parentId === 'object' ? u.parentId?._id : u.parentId;
    return pid ? String(pid) : null;
  };

  const myId = String(user?._id || user?.id || '');

  const forwardableUsers = useMemo(() => {
    if (!nextRole || user?.role === 'sales_executive') return [];
    return allUsers.filter(u => {
      if (u.role !== nextRole) return false;
      if (isSuperAdmin) return true;
      return getParentId(u) === myId;
    });
  }, [allUsers, nextRole, isSuperAdmin, myId, user]);

  const dealersUnderAdmin = useMemo(() => {
    if (!isSuperAdmin || !selectedAdminId) return [];
    return allUsers.filter(u => u.role === 'dealer' && getParentId(u) === selectedAdminId);
  }, [allUsers, isSuperAdmin, selectedAdminId]);

  // For super_admin: also allow forwarding to dealers via admin cascade
  const handleConfirm = () => {
    if (!forwardToId) {
      toast.error(`Please select a ${ROLE_LABELS[nextRole] || 'user'} to forward to.`);
      return;
    }
    onForward({ forwardTo: forwardToId, remark });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-3 py-4">
        <div className="fixed inset-0 bg-black bg-opacity-40" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Forward Leads</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {selectedCount} lead{selectedCount > 1 ? 's' : ''} will be moved to the selected user.
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="p-5 space-y-4 overflow-y-auto">
            {forwardableUsers.length === 0 ? (
              <div className="text-sm text-gray-500 text-center py-6">
                No {ROLE_LABELS[nextRole] || 'users'} available to forward to.
              </div>
            ) : isSuperAdmin ? (
              <>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Select Admin <span className="text-red-500">*</span></label>
                  <select
                    value={selectedAdminId}
                    onChange={e => {
                      const v = e.target.value;
                      setSelectedAdminId(v);
                      // Default forwardTo = admin; dealer can override below if chosen
                      setForwardToId(v);
                    }}
                    className="input text-sm w-full"
                  >
                    <option value="">Select Admin</option>
                    {forwardableUsers.map(u => (
                      <option key={u._id} value={u._id}>{u.firstName} {u.lastName}</option>
                    ))}
                  </select>
                </div>
                {selectedAdminId && (
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Select Dealer (optional)</label>
                    <select
                      value={forwardToId === selectedAdminId ? '' : forwardToId}
                      onChange={e => setForwardToId(e.target.value || selectedAdminId)}
                      className="input text-sm w-full"
                    >
                      <option value="">— Keep with selected Admin —</option>
                      {dealersUnderAdmin.map(u => (
                        <option key={u._id} value={u._id}>{u.firstName} {u.lastName}</option>
                      ))}
                    </select>
                    <p className="text-[11px] text-gray-400 mt-1">
                      Optional: pick a dealer under this admin to forward directly to them. Leave blank to keep with the admin.
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div>
                <label className="block text-xs text-gray-500 mb-1">Forward To <span className="text-red-500">*</span></label>
                <select
                  value={forwardToId}
                  onChange={e => setForwardToId(e.target.value)}
                  className="input text-sm w-full"
                >
                  <option value="">Select {ROLE_LABELS[nextRole]}</option>
                  {forwardableUsers.map(u => (
                    <option key={u._id} value={u._id}>{u.firstName} {u.lastName}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-xs text-gray-500 mb-1">Remark (optional)</label>
              <textarea
                value={remark}
                onChange={e => setRemark(e.target.value)}
                rows={2}
                className="input text-sm w-full resize-none"
                placeholder="Optional note for the receiving user…"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
            <button onClick={onClose} className="btn-outline-secondary text-sm px-5">Cancel</button>
            <button
              onClick={handleConfirm}
              disabled={!forwardToId || loading}
              className="btn-primary text-sm px-6 flex items-center gap-2"
            >
              {loading
                ? <><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />Forwarding…</>
                : `Forward ${selectedCount} Lead${selectedCount !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Query params a tab adds; empty = no tab
const NO_TAB_PARAMS = { status: '', followupDate: '', overdue: '', activeLeads: '' };

const QUALIFICATION_TAG = {
  qualified:     { label: 'Qualified',         cls: 'bg-emerald-100 text-emerald-700' },
  not_qualified: { label: 'Not Qualified',     cls: 'bg-red-100 text-red-700' },
  pending:       { label: 'Qualification Pending', cls: 'bg-gray-100 text-gray-600' },
};

// Attempts a person made on the lead (system entries like "Assignment" don't
// count); older leads fall back to every activity until counters are rebuilt.
const attemptsOf = (lead) => lead.attemptCount ?? lead.activityCount ?? 0;

// Qualification and attempt count, shown under the customer's name
function LeadQualityTags({ lead }) {
  // The same rule as the lead's Lead Status badge (utils/qualification)
  const q = QUALIFICATION_TAG[qualificationOf(lead)];
  const attempts = attemptsOf(lead);
  return (
    <div className="flex flex-wrap items-center gap-1 mt-1">
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${q.cls}`}>
        {q.label}
      </span>
      <span
        className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${attempts ? 'bg-blue-50 text-blue-700' : 'bg-amber-100 text-amber-700'}`}
        title="Activities logged on this lead (calls, visits, closing)"
      >
        {attempts ? `${attempts} attempt${attempts === 1 ? '' : 's'}` : 'No attempt'}
      </span>
    </div>
  );
}

// Module-level reference — persists across renders, reuses the same tab

function WhatsAppLink({ number }) {
  if (!number) return null;
  const digits = number.replace(/\D/g, '');
  return (
    <a
      href={`https://web.whatsapp.com/send?phone=${digits}`}
      target="whatsapp"
      rel="noopener noreferrer"
      className="inline-flex items-center justify-center w-5 h-5 rounded bg-green-500 hover:bg-green-600 transition-colors flex-shrink-0"
      title={`WhatsApp ${number}`}
    >
      <svg viewBox="0 0 24 24" fill="white" className="w-3 h-3">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
      </svg>
    </a>
  );
}

// ── All Indian cities (flat sorted list) ─────────────────────────
const ALL_INDIA_CITIES = [...new Set(Object.values(INDIA_STATES_CITIES).flat())].sort();

// ── Searchable city dropdown ──────────────────────────────────────
function CityFilterDropdown({ value, onChange }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const ref                 = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = search
    ? ALL_INDIA_CITIES.filter(c => c.toLowerCase().includes(search.toLowerCase()))
    : ALL_INDIA_CITIES;

  return (
    <div ref={ref} style={{ position: 'relative', width: 150 }}>
      <button
        type="button"
        onClick={() => { setOpen(v => !v); setSearch(''); }}
        className="input text-sm w-full flex items-center justify-between"
        style={{ textAlign: 'left' }}
      >
        <span className={value ? 'text-gray-800' : 'text-gray-400'}>{value || 'All Cities'}</span>
        <ChevronDownIcon className="w-3 h-3 text-gray-400 flex-shrink-0 ml-1" />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 9999,
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', width: 200, marginTop: 4,
        }}>
          {/* Select All / Clear */}
          <div
            className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 border-b border-gray-100"
            onClick={() => { onChange(''); setOpen(false); }}
          >
            <input type="checkbox" readOnly checked={!value} className="accent-primary-600" />
            <span className="text-sm font-medium text-gray-700">All Cities</span>
          </div>
          {/* Search */}
          <div className="px-3 py-2 border-b border-gray-100">
            <input
              autoFocus
              type="text"
              placeholder="Search City..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full text-sm outline-none text-gray-700 placeholder-gray-400"
            />
          </div>
          {/* List */}
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {filtered.map(city => (
              <div
                key={city}
                className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-primary-50"
                onClick={() => { onChange(city); setOpen(false); setSearch(''); }}
              >
                <input type="checkbox" readOnly checked={value === city} className="accent-primary-600" />
                <span className="text-sm text-gray-700">{city}</span>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-3 text-sm text-gray-400 text-center">No cities found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Lead Category dropdown ────────────────────────────────────────
const LEAD_CATEGORIES = [
  { key: 'hot',  label: 'Hot' },
  { key: 'warm', label: 'Warm' },
  { key: 'cold', label: 'Cold' },
];

function CategoryDropdown({ value, onChange }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const ref                 = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = LEAD_CATEGORIES.filter(c =>
    !search || c.label.toLowerCase().includes(search.toLowerCase())
  );

  const selectedLabel = LEAD_CATEGORIES.find(c => c.key === value)?.label;

  return (
    <div ref={ref} style={{ position: 'relative', width: 150 }}>
      <button
        type="button"
        onClick={() => { setOpen(v => !v); setSearch(''); }}
        className="input text-sm w-full flex items-center justify-between"
        style={{ textAlign: 'left' }}
      >
        <span className={value ? 'text-gray-800' : 'text-gray-400'}>{selectedLabel || 'Lead Category'}</span>
        <ChevronDownIcon className="w-3 h-3 text-gray-400 flex-shrink-0 ml-1" />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 9999,
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', width: 180, marginTop: 4,
        }}>
          {/* Search */}
          <div className="px-3 py-2 border-b border-gray-100">
            <input
              autoFocus
              type="text"
              placeholder="Search Category..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full text-sm outline-none text-gray-700 placeholder-gray-400"
            />
          </div>
          {/* All option */}
          <div
            className="px-3 py-2 cursor-pointer hover:bg-gray-50 text-sm text-gray-500"
            onClick={() => { onChange(''); setOpen(false); }}
          >
            All Categories
          </div>
          {/* Options */}
          {filtered.map(cat => (
            <div
              key={cat.key}
              className={`px-3 py-2 cursor-pointer text-sm hover:bg-primary-50 ${value === cat.key ? 'font-semibold text-primary-600' : 'text-gray-700'}`}
              onClick={() => { onChange(cat.key); setOpen(false); setSearch(''); }}
            >
              {cat.label}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="px-3 py-3 text-sm text-gray-400 text-center">No results</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Activity History Table ────────────────────────────────────────
function ActivityHistoryTable({ activity, activityLoading }) {
  const [search,    setSearch]    = useState('');
  const [sortKey,   setSortKey]   = useState('createdAt');
  const [sortDir,   setSortDir]   = useState('desc');
  const [page,      setPage]      = useState(1);
  const [pageSize,  setPageSize]  = useState(5);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
    setPage(1);
  };

  const filtered = React.useMemo(() => {
    const q = search.toLowerCase();
    return activity.filter(a => {
      if (!q) return true;
      const status   = (a.subTypeName || a.activityTypeName || '').toLowerCase();
      const updater  = a.updatedBy ? `${a.updatedBy.firstName} ${a.updatedBy.lastName}`.toLowerCase() : '';
      const comments = (a.remark || a.metadata?.dealerComments || '').toLowerCase();
      return status.includes(q) || updater.includes(q) || comments.includes(q);
    });
  }, [activity, search]);

  const sorted = React.useMemo(() => {
    return [...filtered].sort((a, b) => {
      let av, bv;
      if (sortKey === 'createdAt')   { av = new Date(a.createdAt);  bv = new Date(b.createdAt); }
      else if (sortKey === 'customDate') { av = a.customDate ? new Date(a.customDate) : 0; bv = b.customDate ? new Date(b.customDate) : 0; }
      else if (sortKey === 'status') { av = (a.subTypeName || a.activityTypeName || '').toLowerCase(); bv = (b.subTypeName || b.activityTypeName || '').toLowerCase(); }
      else if (sortKey === 'updatedBy') { av = a.updatedBy ? `${a.updatedBy.firstName} ${a.updatedBy.lastName}`.toLowerCase() : ''; bv = b.updatedBy ? `${b.updatedBy.firstName} ${b.updatedBy.lastName}`.toLowerCase() : ''; }
      else { av = ''; bv = ''; }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sortKey, sortDir]);

  const effectiveSize = pageSize === 0 ? sorted.length : pageSize;
  const totalPages    = Math.max(1, Math.ceil(sorted.length / effectiveSize));
  const pageRows      = pageSize === 0 ? sorted : sorted.slice((page - 1) * effectiveSize, page * effectiveSize);

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <span className="ml-1 text-gray-300">↕</span>;
    return <span className="ml-1 text-primary-500">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const ExpandableComment = ({ text }) => {
    const [open, setOpen] = useState(false);
    if (!text || text === '—') return <span className="text-gray-400">—</span>;
    if (text.length <= 100) return <span>{text}</span>;
    return (
      <>
        <span>{text.slice(0, 100)}…</span>
        <button onClick={() => setOpen(true)}
          className="ml-1 text-primary-600 hover:text-primary-700 text-xs font-semibold whitespace-nowrap">
          Read more
        </button>
        {open && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 py-8">
              <div className="fixed inset-0 bg-black bg-opacity-40" onClick={() => setOpen(false)} />
              <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[80vh]">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
                  <h3 className="text-sm font-semibold text-gray-900">Comment</h3>
                  <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600">
                    <span className="text-xl leading-none">×</span>
                  </button>
                </div>
                {/* Scrollable body */}
                <div className="overflow-y-auto px-5 py-4">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{text}</p>
                </div>
                {/* Footer */}
                <div className="flex justify-end px-5 py-3 border-t border-gray-200 flex-shrink-0">
                  <button onClick={() => setOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700">
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  const thCls = (col) =>
    `text-left py-2.5 pr-4 text-xs font-semibold text-gray-600 whitespace-nowrap cursor-pointer select-none hover:text-primary-600`;

  if (activityLoading) return <div className="text-center py-4"><Spinner size="sm" /></div>;

  return (
    <div>
      {/* Search + per-page bar */}
      <div className="mb-3 flex items-center gap-3 flex-wrap">
        <input
          type="text"
          placeholder="Search status, updated by, comments..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          className="input text-sm w-72"
        />
        {search && (
          <button onClick={() => { setSearch(''); setPage(1); }} className="text-xs text-gray-400 hover:text-gray-600">
            Clear
          </button>
        )}
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs text-gray-400">{filtered.length} of {activity.length} records</span>
          <select
            value={pageSize}
            onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="input text-xs py-1 w-24"
          >
            <option value={5}>5 / page</option>
            <option value={10}>10 / page</option>
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
            <option value={0}>All</option>
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-gray-400 py-4">No matching records</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className={thCls('status')}    onClick={() => handleSort('status')}>
                    New Status<SortIcon col="status" />
                  </th>
                  <th className="text-left py-2.5 pr-4 text-xs font-semibold text-gray-600 whitespace-nowrap">Previous Status</th>
                  <th className={thCls('createdAt')} onClick={() => handleSort('createdAt')}>
                    Activity Date<SortIcon col="createdAt" />
                  </th>
                  <th className={thCls('customDate')} onClick={() => handleSort('customDate')}>
                    Next Activity Date<SortIcon col="customDate" />
                  </th>
                  <th className={thCls('updatedBy')} onClick={() => handleSort('updatedBy')}>
                    Updated By<SortIcon col="updatedBy" />
                  </th>
                  <th className="text-left py-2.5 text-xs font-semibold text-gray-600 whitespace-nowrap w-64 min-w-[16rem]">Comments</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pageRows.map((a, idx) => {
                  const globalIdx = sorted.indexOf(a);
                  return (
                    <tr key={a._id} className="hover:bg-gray-50">
                      <td className="py-2.5 pr-4 font-medium text-gray-900">{a.subTypeName || a.activityTypeName}</td>
                      <td className="py-2.5 pr-4 text-gray-600">
                        {sorted[globalIdx + 1]?.subTypeName || sorted[globalIdx + 1]?.activityTypeName || '—'}
                      </td>
                      <td className="py-2.5 pr-4 text-gray-600 whitespace-nowrap">
                        {/* When it was logged, on an India clock */}
                        {formatIST(a.createdAt, 'dd MMM yy')}
                        <div className="text-gray-400 text-xs">{formatIST(a.createdAt, 'HH:mm')}</div>
                      </td>
                      <td className="py-2.5 pr-4 text-gray-600 whitespace-nowrap">
                        {/* A chosen date with no time of its own — its India day.
                            Read in a US timezone it showed the day before. */}
                        {a.customDate ? formatDay(istDay(a.customDate), 'dd MMM yy') : '—'}
                      </td>
                      <td className="py-2.5 pr-4 text-gray-600">
                        {a.updatedBy ? `${a.updatedBy.firstName} ${a.updatedBy.lastName}` : '—'}
                      </td>
                      <td className="py-2.5 text-gray-600 w-64 min-w-[16rem]">
                        <ExpandableComment text={a.remark || a.metadata?.dealerComments || '—'} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pageSize !== 0 && (
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-500">
                Showing {(page - 1) * effectiveSize + 1}–{Math.min(page * effectiveSize, sorted.length)} of {sorted.length}
              </p>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(1)} disabled={page === 1}
                  className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-30 hover:bg-gray-50">«</button>
                <button onClick={() => setPage(p => p - 1)} disabled={page === 1}
                  className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-30 hover:bg-gray-50">‹</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                  .reduce((acc, p, i, arr) => {
                    if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) => p === '...' ? (
                    <span key={`ellipsis-${i}`} className="px-2 py-1 text-xs text-gray-400">…</span>
                  ) : (
                    <button key={p} onClick={() => setPage(p)}
                      className={`px-2.5 py-1 text-xs rounded border ${page === p ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-200 hover:bg-gray-50'}`}>
                      {p}
                    </button>
                  ))
                }
                <button onClick={() => setPage(p => p + 1)} disabled={page === totalPages}
                  className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-30 hover:bg-gray-50">›</button>
                <button onClick={() => setPage(totalPages)} disabled={page === totalPages}
                  className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-30 hover:bg-gray-50">»</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────

export const Leads = () => {
  const location = useLocation();
  const { hasPermission, user } = useAuth();
  const isAdminOrManager = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'dealer';
  const queryClient = useQueryClient();

  const pageConfig = (() => {
    const p = new URLSearchParams(location.search);
    const followupDate     = p.get('followupDate')     || '';
    const enquiryDate      = p.get('enquiryDate')      || '';
    const status           = p.get('status')           || '';
    const pendingFollowups = p.get('pendingFollowups') || '';
    const todayStr     = todayIST();
    const yesterdayStr = yesterdayIST();
    if (enquiryDate === todayStr)
      return { title: "Today's Enquiries",     subtitle: 'Leads with enquiry date today.',     crumb: "Today's Enquiries" };
    if (enquiryDate === yesterdayStr)
      return { title: "Yesterday's Enquiries", subtitle: 'Leads with enquiry date yesterday.', crumb: "Yesterday's Enquiries" };
    if (followupDate === todayStr)
      return { title: "Today's Enquiries",     subtitle: 'Enquiries with follow-up date set to today.',     crumb: "Today's Enquiries" };
    if (followupDate === yesterdayStr)
      return { title: "Yesterday's Enquiries", subtitle: "Enquiries with follow-up date set to yesterday.",  crumb: "Yesterday's Enquiries" };
    if (pendingFollowups === 'true')
      return { title: 'Pending Follow-ups',    subtitle: "Today's follow-ups and any past-due (overdue) leads.", crumb: 'Pending Follow-ups' };
    if (status === 'follow_up')
      return { title: 'Pending Follow-ups',    subtitle: 'Leads currently in follow-up status.',            crumb: 'Pending Follow-ups' };
    return   { title: 'All Enquiries',         subtitle: 'This section provides access to all available enquiries.', crumb: null };
  })();

  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin      = user?.role === 'admin';
  const isDealer     = user?.role === 'dealer';
  const canFilterByUser = isSuperAdmin || isAdmin || isDealer;

  const parseFiltersFromUrl = () => {
    const p = new URLSearchParams(location.search);
    return {
      search:            p.get('search')            || '',
      phone:             p.get('phone')             || '',
      status:            p.get('status')            || '',
      convertedSubType:  p.get('convertedSubType')  || '',
      activityTypeKey:   p.get('activityTypeKey')   || '',
      activitySubTypeKey:p.get('activitySubTypeKey')|| '',
      category:          p.get('category')          || '',
      dateType:          p.get('dateType')          || 'created',
      startDate:         p.get('startDate')         || '',
      endDate:           p.get('endDate')            || '',
      brand:             p.get('brand')              || '',
      model:             p.get('model')              || '',
      city:              p.get('city')               || '',
      source:            p.get('source')             || '',
      followupDate:      p.get('followupDate')       || '',
      enquiryDate:       p.get('enquiryDate')        || '',
      assignedTo:        p.get('assignedTo')         || '',
      dealerId:          p.get('dealerId')           || '',
      toBeAssigned:      p.get('toBeAssigned')       || '',
      qualificationStatus: p.get('qualificationStatus') || '',
      pendingFollowups:  p.get('pendingFollowups')   || '',
      duplicates:        p.get('duplicates')         || '',
      overdue:           p.get('overdue')            || '',
      noFollowup:        p.get('noFollowup')         || '',
      hasTestDrive:      p.get('hasTestDrive')       || '',
      crmSync:           p.get('crmSync')            || '',
    };
  };

  const [activeTab, setActiveTab]       = useState('all');
  const [tabParams, setTabParams]       = useState(NO_TAB_PARAMS);
  const [filters, setFiltersRaw]        = useState(parseFiltersFromUrl);
  // Changing a filter is a new list: back to page 1 and drop the selection.
  // Paging on with the old list's cursor returned a wrong or empty page.
  const setFilters = (next) => {
    setFiltersRaw(next);
    setPageNum(1);
    setCursorStack([null]);
    setSelectedLeads(new Set());
  };
  const [moreFilters, setMoreFilters]   = useState(false);
  const [selectedAdminForFilter, setSelectedAdminForFilter]       = useState('');
  const [selectedDealerForFilter, setSelectedDealerForFilter]     = useState('');
  const [selectedSalesExecForFilter, setSelectedSalesExecForFilter] = useState('');
  // Picking a dealer can mean two different things, and the counts differ:
  // with the team it is everything that dealer's business owns (the same set
  // the Dashboard's dealer chip counts); without it, only the leads still
  // sitting in the dealer's own bucket, not yet forwarded to a sales
  // executive. Defaults to the team, because that is what a dealer's name
  // normally means to the person reading the number.
  const [dealerIncludesTeam, setDealerIncludesTeam] = useState(true);
  const [expandedLeadId, setExpandedLeadId] = useState(() => {
    const p = new URLSearchParams(location.search);
    return p.get('expanded') || null;
  });
  const tableRef = React.useRef(null);

  // ── Multi-select & bulk assign ─────────────────────────────────
  const [selectedLeads, setSelectedLeads]         = useState(new Set());
  const [showBulkAssign, setShowBulkAssign]       = useState(false);
  const [showBulkForward, setShowBulkForward]     = useState(false);
  const [showTransfer, setShowTransfer]           = useState(false);

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelectedLeads(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (e) => {
    e.stopPropagation();
    if (selectedLeads.size === leads.length) {
      setSelectedLeads(new Set());
    } else {
      setSelectedLeads(new Set(leads.map(l => l._id)));
    }
  };

  const bulkAssignMutation = useMutation(
    ({ assignedToList, remark }) => leadService.bulkAssign({
      leadIds: Array.from(selectedLeads),
      assignedToList,
      remark,
    }),
    {
      onSuccess: (res) => {
        toast.success(res.data.message || 'Leads assigned successfully');
        setSelectedLeads(new Set());
        setShowBulkAssign(false);
        queryClient.invalidateQueries('leads');
        queryClient.invalidateQueries('leadTabStats');
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Bulk assign failed');
      },
    }
  );

  const bulkDeleteMutation = useMutation(
    () => leadService.bulkDelete({ leadIds: Array.from(selectedLeads) }),
    {
      onSuccess: (res) => {
        toast.success(res.data.message || 'Leads deleted successfully');
        setSelectedLeads(new Set());
        queryClient.invalidateQueries('leads');
        queryClient.invalidateQueries('leadTabStats');
        queryClient.invalidateQueries('leadTrash');   // they're in the Recycle Bin now
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Bulk delete failed');
      },
    }
  );

  const handleBulkDelete = () => {
    const count = selectedLeads.size;
    if (!count) return;
    if (!window.confirm(`Are you sure you want to delete ${count} lead${count > 1 ? 's' : ''}? They move to the Recycle Bin with all their data and can be restored for 7 days.`)) {
      return;
    }
    bulkDeleteMutation.mutate();
  };

  const bulkForwardMutation = useMutation(
    ({ forwardTo, remark }) => leadService.bulkForward({
      leadIds: Array.from(selectedLeads),
      forwardTo,
      remark,
    }),
    {
      onSuccess: (res) => {
        toast.success(res.data.message || 'Leads forwarded successfully');
        setSelectedLeads(new Set());
        setShowBulkForward(false);
        queryClient.invalidateQueries('leads');
        queryClient.invalidateQueries('leadTabStats');
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Bulk forward failed');
      },
    }
  );

  // Company-only: copy/forward selected leads to another workspace
  const transferMutation = useMutation(
    ({ targetTenantId, mode }) => companyService.transferLeads({
      sourceTenantId: getWorkspaceId(),
      targetTenantId,
      leadIds: Array.from(selectedLeads),
      mode,
    }),
    {
      onSuccess: (res) => {
        toast.success(res.data.message || 'Leads transferred');
        setSelectedLeads(new Set());
        setShowTransfer(false);
        queryClient.invalidateQueries('leads');
        queryClient.invalidateQueries('leadTabStats');
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Transfer failed');
      },
    }
  );

  // Cursor-based pagination
  const [pageNum, setPageNum]         = useState(1);
  const [cursorStack, setCursorStack] = useState([null]); // index = page-1, value = cursor
  const [leadsPerPage, setLeadsPerPage] = useState(10);

  const currentCursor = cursorStack[pageNum - 1] ?? null;

  // A sidebar link (Show / Today's / Yesterday's Enquiries, Pending Follow-ups,
  // Duplicates) is a fresh view: its own filters, page 1, and no tab carried
  // over — a "Today's Task" tab left on used to add its follow-up date to
  // Today's Enquiries, and the server read that instead of the enquiry date.
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    setFiltersRaw(parseFiltersFromUrl());
    setActiveTab('all');
    setTabParams(NO_TAB_PARAMS);
    setExpandedLeadId(p.get('expanded') || null);
    setSelectedLeads(new Set());
    setPageNum(1);
    setCursorStack([null]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleLeadsPerPageChange = (newSize) => {
    setLeadsPerPage(newSize);
    setPageNum(1);
    setCursorStack([null]);
  };

  // ── Queries ──────────────────────────────────────────────────

  // Only apply non-empty tabParams so they don't overwrite user filter values
  const activeTabParams = Object.fromEntries(Object.entries(tabParams).filter(([, v]) => v !== ''));

  const { data: leadsData, isLoading, isFetching } = useQuery(
    ['leads', filters, activeTabParams, pageNum, leadsPerPage],
    () => leadService.getLeads({ ...filters, ...activeTabParams, cursor: currentCursor || undefined, limit: leadsPerPage }),
    { keepPreviousData: true }
  );

  const { data: activityData, isLoading: activityLoading } = useQuery(
    ['leadActivities', expandedLeadId],
    () => leadActivityService.getLeadActivities(expandedLeadId),
    { enabled: !!expandedLeadId, staleTime: 30_000 }
  );

  // Stats filters use ONLY user-applied filters (not tab params)
  // so tab counts always show the full breakdown within the user's search context
  const statsFilters = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v !== '' && v != null)
  );

  const { data: statsData } = useQuery(
    ['leadTabStats', statsFilters],
    () => leadService.getLeadStats(statsFilters),
    { staleTime: 0 }
  );
  const tabCounts = statsData?.data?.data?.stats || statsData?.data?.stats || {};

  const { data: usersData } = useQuery(
    'filterUsers',
    () => userService.getUsers({ status: 'active', limit: 300 }),
    { enabled: canFilterByUser, staleTime: 5 * 60_000 }
  );

  const allFilterUsers = (() => {
    const d = usersData?.data;
    return d?.users || d?.data?.users || [];
  })();

  // Brands list for the More Filters Brand/Model cascading dropdowns
  const { data: brandsData } = useQuery('brandsForFilter', brandService.getBrands, { staleTime: 10 * 60_000 });
  const brandsList = brandsData?.data?.data?.brands || brandsData?.data?.brands || [];
  const filterModels = (() => {
    if (!filters.brand) return [];
    const b = brandsList.find(x => x.name === filters.brand);
    return (b?.models || []).filter(m => m.isActive !== false);
  })();

  const adminFilterList = allFilterUsers.filter(u => u.role === 'admin');
  const dealerFilterList = (() => {
    const allDealers = allFilterUsers.filter(u => u.role === 'dealer');
    // If an admin is picked in the cascade, narrow dealers to that admin's downline
    if (isSuperAdmin && selectedAdminForFilter) {
      return allDealers.filter(u => {
        const pid = typeof u.parentId === 'object' ? u.parentId?._id : u.parentId;
        return String(pid) === String(selectedAdminForFilter);
      });
    }
    return allDealers;
  })();
  const salesExecFilterList = (() => {
    const allExecs = allFilterUsers.filter(u => u.role === 'sales_executive');
    if ((isSuperAdmin || isAdmin) && selectedDealerForFilter) {
      return allExecs.filter(u => {
        const pid = typeof u.parentId === 'object' ? u.parentId?._id : u.parentId;
        return String(pid) === String(selectedDealerForFilter);
      });
    }
    return allExecs;
  })();

  // ── Mutations ────────────────────────────────────────────────

  const deleteLeadMutation = useMutation(leadService.deleteLead, {
    onSuccess: () => {
      toast.success('Lead deleted');
      queryClient.invalidateQueries('leads');
      queryClient.invalidateQueries('leadTabStats');   // the tab counts, with the list
      queryClient.invalidateQueries('leadTrash');      // it's in the Recycle Bin now
      setExpandedLeadId(null);
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to delete lead'),
  });

  // ── Derived data ─────────────────────────────────────────────

  let leads = [];
  let pagination = {};
  if (leadsData?.data) {
    if (leadsData.data.leads)              leads = leadsData.data.leads;
    else if (Array.isArray(leadsData.data)) leads = leadsData.data;
    else if (leadsData.data.data?.leads)   leads = leadsData.data.data.leads;
    pagination = leadsData.data.pagination || leadsData.data.data?.pagination || {};
  }

  const activity = (() => {
    const d = activityData?.data;
    return d?.data?.activities || d?.activities || [];
  })();

  const totalCount = pagination.total ?? null;

  // Admins and the super admin: which of these leads reached a dealer's CRM.
  const crmStatus = useCrmSyncStatus(leads.map(l => l._id), user);

  // ── Handlers ────────────────────────────────────────────────

  const goNext = () => {
    if (!pagination.nextCursor && !pagination.hasMore) return;
    setCursorStack(prev => {
      const next = [...prev];
      next[pageNum] = pagination.nextCursor;
      return next;
    });
    setPageNum(p => p + 1);
    setExpandedLeadId(null);
  };

  const goPrev = () => {
    if (pageNum <= 1) return;
    setPageNum(p => p - 1);
    setExpandedLeadId(null);
  };

  const handleFilterChange = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

  // Which filter keys express "this dealer", given the two meanings above.
  // The API reads assignedTo as an exact owner and dealerId as the dealer plus
  // their downline, and takes assignedTo first — so exactly one of them is
  // ever set.
  const dealerScopeFilters = (dealerId, withTeam) => {
    if (!dealerId) return { dealerId: '', assignedTo: selectedAdminForFilter || '' };
    return withTeam
      ? { dealerId, assignedTo: '' }
      : { dealerId: '', assignedTo: dealerId };
  };

  const BLANK_FILTERS = { search: '', phone: '', status: '', convertedSubType: '', activityTypeKey: '', activitySubTypeKey: '', category: '', dateType: 'created', startDate: '', endDate: '', brand: '', model: '', city: '', source: '', assignedTo: '', dealerId: '', toBeAssigned: '', qualificationStatus: '', pendingFollowups: '', enquiryDate: '', duplicates: '', overdue: '', noFollowup: '', hasTestDrive: '', crmSync: '' };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setPageNum(1);
    setCursorStack([null]);
    const todayStr = todayIST();   // the India day the server counts "Today's Task" by
    const tabMap = {
      all:       { status: '',          followupDate: '',       overdue: '',     activeLeads: ''     },
      active:    { status: '',          followupDate: '',       overdue: '',     activeLeads: 'true' },
      lost:      { status: 'lost',      followupDate: '',       overdue: '',     activeLeads: ''     },
      new:       { status: 'new',       followupDate: '',       overdue: '',     activeLeads: ''     },
      today:     { status: '',          followupDate: todayStr, overdue: '',     activeLeads: ''     },
      overdue:   { status: '',          followupDate: '',       overdue: 'true', activeLeads: ''     },
      converted: { status: 'converted', followupDate: '',       overdue: '',     activeLeads: ''     },
    };
    setTabParams(tabMap[tab] || NO_TAB_PARAMS);
  };

  const handleReset = () => {
    setActiveTab('all');
    setTabParams(NO_TAB_PARAMS);
    setFilters(BLANK_FILTERS);
    setSelectedAdminForFilter('');
    setSelectedDealerForFilter('');
    setSelectedSalesExecForFilter('');
    setDealerIncludesTeam(true);
    setPageNum(1);
    setCursorStack([null]);
  };

  const toggleExpand = (leadId) => {
    setExpandedLeadId(prev => {
      if (prev !== leadId) leadService.markViewed(leadId).catch(() => {});
      return prev === leadId ? null : leadId;
    });
  };

  useEffect(() => {
    if (expandedLeadId && tableRef.current) {
      tableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [expandedLeadId]);

  const [isExporting, setIsExporting]       = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  const handleDownload = async () => {
    setIsExporting(true);
    try {
      const res  = await leadService.exportLeads({ ...filters, ...activeTabParams });
      const data = res?.data?.data?.leads || res?.data?.leads || [];

      if (!data.length) { toast.error('No leads to export'); return; }

      const sd  = (v, fmt) => { try { return v ? formatIST(v, fmt) : ''; } catch { return ''; } };   // India clock
      const CAT = { immediate: 'Hot', within_1_month: 'Warm', within_3_months: 'Cold', within_6_months: 'Cold', just_enquiring: 'Cold' };

      const headers = [
        // Customer Info
        'Enquiry Date', 'Name', 'Phone', 'Alternate Phone', 'Email', 'State', 'City', 'Subcity', 'Pincode', 'Address',
        // Lead Info
        'Brand', 'Model', 'Lead Category', 'Buying Timeline', 'Source',
        // Status
        'Status', 'Qualification', 'Attempts', 'Converted Type', 'Is Closed', 'Priority', 'Current Stage', 'Notes', 'Tags',
        // Activity / Remark
        'Last Activity', 'Remarks', 'Follow-ups Count', 'Next Follow-up',
        // Booking Details (Model Summary)
        'Booking Customer Name', 'Booking Customer Phone', 'Booking Model', 'Booking Date', 'Retail Date',
        // Test Drive Details
        'Test Drive Location', 'Test Drive Result',
        // Home Visit
        'Home Visit Address',
        // Lost Details
        'Lost Reason', 'Not Purchase Reason',
        // Assignment
        'Assigned To', 'Assigned Role',
        // When the lead last reached a dealer, and which one (from its assignment history)
        'Dealer Assign Date', 'Assigned Dealer',
        // Dealer Details
        'Dealer Name', 'Dealer City', 'Dealer Phone', 'Dealer Email',
        // Meta
        'Created By', 'Created At', 'Updated At',
      ];

      const dataRows = data.map((l) => {
        const acts      = l._acts || {};
        const booked    = acts['booked']              || {};
        const tdSched   = acts['test_drive_schedule'] || {};
        const tdDone    = acts['test_drive_completed']|| {};
        const homeVisit = acts['home_visit']          || {};
        const lostCo    = acts['lost_to_codealers']   || {};
        const notPurch  = acts['not_purchased']       || {};

        return [
          sd(l.leadInfo?.enquiryDate, 'dd-MM-yyyy'),
          l.customerInfo?.name            || '',
          l.customerInfo?.phone           || '',
          l.customerInfo?.alternatePhone  || '',
          l.customerInfo?.email           || '',
          l.customerInfo?.state           || '',
          l.customerInfo?.city            || '',
          l.customerInfo?.subcity         || '',
          l.customerInfo?.pincode         || '',
          l.customerInfo?.address         || '',
          cleanDisplay(l.leadInfo?.brand),
          cleanDisplay(l.leadInfo?.model),
          CAT[l.leadInfo?.buyingTime]     || '',
          buyingTimeLabel(l.leadInfo),
          l.leadInfo?.source              || '',
          STATUS_CONFIG[l.status]?.label  || l.status || '',
          l.qualificationStatus === 'qualified'     ? 'Qualified'
            : l.qualificationStatus === 'not_qualified' ? 'Not Qualified'
            : 'Pending',
          attemptsOf(l),
          l.convertedSubType              || '',
          l.isPermanentlyClosed ? 'Yes'   : 'No',
          PRIORITY_CONFIG[l.priority]?.label || l.priority || '',
          l.currentStage                  || '',
          l.notes                         || '',
          (l.tags || []).join(', '),
          l.lastActivitySubTypeName || l.lastActivityTypeKey || '',
          (l._remarks || []).map(r => `${sd(r.createdAt, 'dd-MM-yyyy HH:mm')} - ${r.remark}`).join('\n') || '',
          l._followupCount ?? 0,
          sd(l.nextFollowupDate, 'dd-MM-yyyy'),
          booked.metadata?.customerName   || '',
          booked.metadata?.customerPhone  || '',
          booked.metadata?.bookingModel   || '',
          sd(booked.metadata?.bookingDate, 'dd-MM-yyyy'),
          sd(booked.metadata?.retailDate,  'dd-MM-yyyy'),
          tdSched.metadata?.location      || '',
          tdDone.metadata?.testDriveResult|| '',
          homeVisit.metadata?.address     || '',
          lostCo.metadata?.lostReason     || '',
          notPurch.metadata?.notPurchaseReason || '',
          l.assignment?.assignedTo
            ? `${l.assignment.assignedTo.firstName} ${l.assignment.assignedTo.lastName}`
            : 'Unassigned',
          l.assignment?.assignedRole      || '',
          sd(l._dealerAssignedAt, 'dd-MM-yyyy HH:mm'),
          l._dealerAssignedTo             || '',
          l.dealerInfo?.dealerName        || '',
          l.dealerInfo?.dealerCity        || '',
          l.dealerInfo?.dealerNumber      || '',
          l.dealerInfo?.dealerEmail       || '',
          l.createdBy ? `${l.createdBy.firstName} ${l.createdBy.lastName}` : '',
          sd(l.createdAt, 'dd-MM-yyyy HH:mm'),
          sd(l.updatedAt, 'dd-MM-yyyy HH:mm'),
        ];
      });

      const ws = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);

      const remarksColIdx = headers.indexOf('Remarks');
      // Enable wrap text on all Remarks cells so newlines render in Excel
      if (remarksColIdx >= 0) {
        dataRows.forEach((_, rowIdx) => {
          const cellAddr = XLSX.utils.encode_cell({ r: rowIdx + 1, c: remarksColIdx });
          if (ws[cellAddr]) ws[cellAddr].s = { alignment: { wrapText: true, vertical: 'top' } };
        });
      }

      ws['!cols'] = headers.map((h, col) => ({
        wch: col === remarksColIdx ? 50 : Math.max(h.length, ...dataRows.map(r => {
          const v = String(r[col] ?? '');
          // For multiline cells use the longest line length
          return Math.max(...v.split('\n').map(l => l.length));
        })) + 2,
      }));

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Leads');
      XLSX.writeFile(wb, `leads-${formatDay(todayIST(), 'dd-MM-yyyy')}.xlsx`);
      toast.success(`Downloaded ${data.length} leads`);
    } catch (err) {
      console.error('[Export error]', err);
      toast.error(`Export failed: ${err.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────

  return (
    <div>
      {/* ── Header ── */}
      <div className="mb-3">
        {/* Breadcrumb */}
        <nav className="text-xs text-gray-400 mb-2 flex items-center gap-1">
          <span>Home</span>
          <span>›</span>
          <span>Enquiries</span>
          {pageConfig.crumb && (
            <>
              <span>›</span>
              <span className="text-gray-600">{pageConfig.crumb}</span>
            </>
          )}
        </nav>

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{pageConfig.title}</h1>
          <p className="text-sm text-gray-400 mt-0.5">{pageConfig.subtitle}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:flex-shrink-0 sm:mt-1 sm:self-start">
          {hasPermission('leads', 'create') && (
            <Link to="/leads/new" className="btn-primary text-xs">
              <PlusIcon className="h-3.5 w-3.5 sm:mr-1" />
              <span className="hidden sm:inline">Add Lead</span>
            </Link>
          )}
          {hasPermission('leads', 'bulkUpload') && (
            <button onClick={() => setShowBulkUpload(true)} className="btn-outline-primary text-xs">
              <DocumentArrowUpIcon className="h-3.5 w-3.5 sm:mr-1" />
              <span className="hidden sm:inline">Bulk Upload</span>
            </button>
          )}
          <button onClick={handleDownload} disabled={isExporting} className="btn-outline-primary text-xs">
            <ArrowDownTrayIcon className="h-3.5 w-3.5 sm:mr-1" />
            <span className="hidden sm:inline">{isExporting ? 'Exporting...' : 'Download'}</span>
          </button>

        </div>
      </div>
      </div>

      {/* ── Filter bar ── */}
      <div className="bg-white shadow-sm rounded-xl border border-gray-100 mb-4 px-5 py-4 space-y-2">

        {/* Row 1 — filter inputs + action buttons in one flex-wrap row */}
        <div className="flex flex-wrap items-center gap-2">
          <input type="text" placeholder="Name" value={filters.search}
            onChange={e => handleFilterChange('search', e.target.value)}
            className="input text-sm w-full sm:w-32" />
          <input type="text" placeholder="Mobile No." value={filters.phone}
            onChange={e => handleFilterChange('phone', e.target.value)}
            className="input text-sm w-full sm:w-32" />
          <select
            value={filters.activeLeads ? 'active_leads' : (filters.activitySubTypeKey || filters.activityTypeKey || filters.status)}
            onChange={e => {
              const val = e.target.value;
              const STATUS_VALS = ['', 'new', 'in_progress', 'follow_up', 'converted', 'lost'];
              const TYPE_VALS   = ['followup_lead', 'face_to_face'];
              if (val === 'active_leads') {
                setFilters(prev => ({ ...prev, status: '', activityTypeKey: '', activitySubTypeKey: '', convertedSubType: '', activeLeads: true }));
              } else if (STATUS_VALS.includes(val)) {
                setFilters(prev => ({ ...prev, status: val, activityTypeKey: '', activitySubTypeKey: '', convertedSubType: '', activeLeads: false }));
              } else if (TYPE_VALS.includes(val)) {
                setFilters(prev => ({ ...prev, status: '', activityTypeKey: val, activitySubTypeKey: '', convertedSubType: '', activeLeads: false }));
              } else {
                setFilters(prev => ({ ...prev, status: '', activityTypeKey: '', activitySubTypeKey: val, convertedSubType: '', activeLeads: false }));
              }
            }}
            className="input text-sm w-full sm:w-48">
            <option value="">All Status</option>
            <option value="active_leads">Active Leads</option>
            <option value="follow_up">Follow Up</option>
            <option value="face_to_face">Face to Face</option>
            <option value="converted">Converted</option>
            <option value="lost">Lost</option>
            <optgroup label="── Follow Up Sub-types ──" disabled />
            <option value="call_back">Call Back</option>
            <option value="ask_to_call_later">Ask to Call Later</option>
            <option value="ringing_not_responding">Ringing Not Responding</option>
            <option value="not_reachable">Not Reachable</option>
            <option value="switch_off">Switch Off</option>
            <optgroup label="── Face to Face Sub-types ──" disabled />
            <option value="home_visit">Home Visit</option>
            <option value="test_drive_schedule">Test Drive Schedule</option>
            <option value="showroom_visit">Showroom Visit</option>
            <option value="test_drive_completed">Test Drive Completed</option>
            <optgroup label="── Converted Sub-types ──" disabled />
            <option value="booked">Booked</option>
            <option value="retail">Retail</option>
            <optgroup label="── Lost Sub-types ──" disabled />
            <option value="lost_to_codealers">Lost to Co-Dealers</option>
            <option value="lost_to_competitions">Lost to Competitors</option>
            <option value="not_purchased">Not Purchase</option>
            <option value="existing">Existing</option>
          </select>
          <select value={filters.dateType} onChange={e => handleFilterChange('dateType', e.target.value)}
            className="input text-sm w-full sm:w-36">
            <option value="created">By Create Date</option>
            <option value="updated">By Update Date</option>
            <option value="scheduled">By Scheduled Date</option>
          </select>
          <input type="date" value={filters.startDate} onChange={e => handleFilterChange('startDate', e.target.value)}
            className="input text-sm w-40" />
          <input type="date" value={filters.endDate} onChange={e => handleFilterChange('endDate', e.target.value)}
            className="input text-sm w-40" />
          {/* Buttons — grouped so they always wrap together */}
          <div className="flex items-center gap-2 flex-none">
            <button onClick={() => { queryClient.invalidateQueries('leads'); queryClient.invalidateQueries('leadTabStats'); }} className="btn-outline-primary text-sm px-5">
              Search
            </button>
            <button onClick={handleReset} className="btn-outline-secondary text-sm px-5">
              Reset
            </button>
            <button onClick={() => setMoreFilters(v => !v)}
              className="text-sm font-semibold text-primary-600 hover:text-primary-700 whitespace-nowrap bg-transparent border-none p-0">
              {moreFilters ? 'Hide Filters' : 'More Filters'}
            </button>
          </div>
        </div>

        {/* Row 2 — expanded More Filters */}
        {moreFilters && (
          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-gray-100">
            <select
              value={filters.brand}
              onChange={e => {
                const v = e.target.value;
                setFilters(prev => ({ ...prev, brand: v, model: '' }));
              }}
              className="input text-sm w-full sm:w-36"
            >
              <option value="">All Brands</option>
              {brandsList.filter(b => b.isActive !== false).map(b => (
                <option key={b._id || b.name} value={b.name}>{b.name}</option>
              ))}
            </select>
            <select
              value={filters.model}
              onChange={e => handleFilterChange('model', e.target.value)}
              className="input text-sm w-full sm:w-36"
              disabled={!filters.brand}
            >
              <option value="">{filters.brand ? 'All Models' : 'Select Brand first'}</option>
              {filterModels.map(m => (
                <option key={m._id || m.name} value={m.name}>{m.name}</option>
              ))}
            </select>
            <CityFilterDropdown value={filters.city} onChange={v => handleFilterChange('city', v)} />
            <CategoryDropdown value={filters.category} onChange={v => handleFilterChange('category', v)} />
            <select
              value={filters.source}
              onChange={e => handleFilterChange('source', e.target.value)}
              className="input text-sm w-full sm:w-36"
            >
              <option value="">All Sources</option>
              {Object.entries(SOURCE_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            {/* Admin filter — super admin only — exact match on assignedTo */}
            {isSuperAdmin && (
              <select
                value={selectedAdminForFilter}
                onChange={e => {
                  const val = e.target.value;
                  setSelectedAdminForFilter(val);
                  // Reset downline cascades
                  setSelectedDealerForFilter('');
                  setSelectedSalesExecForFilter('');
                  setDealerIncludesTeam(true);
                  // Exact assignedTo — only leads currently in this admin's own bucket
                  setFilters(prev => ({ ...prev, assignedTo: val, dealerId: '' }));
                }}
                className="input text-sm w-full sm:w-40"
              >
                <option value="">All Admins</option>
                {adminFilterList.map(a => (
                  <option key={a._id} value={a._id}>{a.firstName} {a.lastName}</option>
                ))}
              </select>
            )}
            {/* Dealer filter — super admin & admin only — dealer + their team
                by default, with a toggle for the dealer's own bucket alone */}
            {(isSuperAdmin || isAdmin) && (
              <select
                value={selectedDealerForFilter}
                onChange={e => {
                  const val = e.target.value;
                  setSelectedDealerForFilter(val);
                  setSelectedSalesExecForFilter('');
                  setFilters(prev => ({ ...prev, ...dealerScopeFilters(val, dealerIncludesTeam) }));
                }}
                className="input text-sm w-full sm:w-40"
              >
                <option value="">All Dealers</option>
                {dealerFilterList.map(d => (
                  <option key={d._id} value={d._id}>{d.firstName} {d.lastName}</option>
                ))}
              </select>
            )}
            {(isSuperAdmin || isAdmin) && selectedDealerForFilter && (
              <label
                className="flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap"
                title="On: every lead this dealer's team owns, matching the Dashboard. Off: only the leads still in the dealer's own bucket, not yet forwarded to a sales executive."
              >
                <input
                  type="checkbox"
                  checked={dealerIncludesTeam}
                  onChange={e => {
                    const withTeam = e.target.checked;
                    setDealerIncludesTeam(withTeam);
                    // A sales-exec filter is narrower than either meaning, so
                    // the toggle would do nothing until it is cleared.
                    setSelectedSalesExecForFilter('');
                    setFilters(prev => ({ ...prev, ...dealerScopeFilters(selectedDealerForFilter, withTeam) }));
                  }}
                  className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                Include team
              </label>
            )}
            {/* Sales Exec filter — super admin, admin, dealer (independent) */}
            {canFilterByUser && (
              <select
                value={selectedSalesExecForFilter}
                onChange={e => {
                  const val = e.target.value;
                  setSelectedSalesExecForFilter(val);
                  // Exact assignedTo for a sales exec; clearing it falls back
                  // to the dealer (team or bucket, as selected), then the admin
                  setFilters(prev => val
                    ? { ...prev, assignedTo: val, dealerId: '' }
                    : { ...prev, ...dealerScopeFilters(selectedDealerForFilter, dealerIncludesTeam) });
                }}
                className="input text-sm w-full sm:w-44"
              >
                <option value="">All Sales Exec</option>
                {salesExecFilterList.map(se => (
                  <option key={se._id} value={se._id}>{se.firstName} {se.lastName}</option>
                ))}
              </select>
            )}

            {/* "To Be Assigned" — leads still sitting at the current user's level */}
            <label className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                checked={filters.toBeAssigned === 'true'}
                onChange={e => setFilters(prev => ({
                  ...prev,
                  toBeAssigned: e.target.checked ? 'true' : '',
                  // Clearing other user filters so this one is unambiguous
                  assignedTo: e.target.checked ? '' : prev.assignedTo,
                  dealerId:   e.target.checked ? '' : prev.dealerId,
                }))}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              To Be Assigned
            </label>

            {/* Qualification filter */}
            <select
              value={filters.qualificationStatus}
              onChange={e => handleFilterChange('qualificationStatus', e.target.value)}
              className="input text-sm w-full sm:w-44"
            >
              <option value="">All Qualification</option>
              <option value="pending">Pending</option>
              <option value="qualified">Qualified</option>
              <option value="not_qualified">Not Qualified</option>
            </select>

            {/* Dealer CRM sync — admins see their own team's leads */}
            {canSeeCrmSync(user) && (
              <select
                value={filters.crmSync}
                onChange={e => handleFilterChange('crmSync', e.target.value)}
                className="input text-sm w-full sm:w-44"
              >
                <option value="">All CRM Sync</option>
                <option value="sent">Sent to CRM</option>
                <option value="pending">Sending to CRM</option>
                <option value="failed">CRM sync failed</option>
              </select>
            )}
          </div>
        )}
      </div>

      {/* ── Tabs — hidden on filtered sidebar pages (Today's/Yesterday's/Pending/Duplicates) ── */}
      {!filters.followupDate && !filters.enquiryDate && filters.pendingFollowups !== 'true' && filters.duplicates !== 'true' && filters.overdue !== 'true' && filters.noFollowup !== 'true' && filters.hasTestDrive !== 'true' && <div className="flex items-center gap-0 mb-3 border-b border-gray-200 overflow-x-auto">
        {[
          { key: 'all',       label: 'All Leads',     count: tabCounts.totalLeads },
          { key: 'active',    label: 'Active Leads',  count: tabCounts.activeLeads },
          { key: 'lost',      label: 'Lost Leads',    count: tabCounts.lostLeads },
          { key: 'new',       label: 'New Lead',      count: tabCounts.newLeads },
          { key: 'today',     label: "Today's Task",  count: tabCounts.todayTasks },
          { key: 'overdue',   label: 'Overdue',       count: tabCounts.overdue },
          { key: 'converted', label: 'Converted',     count: tabCounts.convertedLeads },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => handleTabClick(tab.key)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {tab.label}
            {tab.count != null && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                activeTab === tab.key
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {tab.count >= 1000 ? `${(tab.count / 1000).toFixed(1)}K` : tab.count}
              </span>
            )}
          </button>
        ))}
      </div>}

      {/* ── Count line ── */}
      {totalCount != null && (
        <p className="text-sm font-semibold text-gray-700 mb-3">
          {pageConfig.title} (Total {totalCount.toLocaleString()} Available)
        </p>
      )}

      {/* ── Table ── */}
      <div className="bg-white shadow-sm rounded-xl border border-gray-100 overflow-hidden relative">
        {/* Refetch overlay — appears when filters/search change while old data is still showing */}
        {isFetching && !isLoading && (
          <div className="absolute inset-0 z-10 bg-white/60 backdrop-blur-[1px] flex items-center justify-center pointer-events-none">
            <div className="bg-white shadow-md rounded-full px-4 py-2 flex items-center gap-2 border border-gray-100">
              <Spinner size="sm" />
              <span className="text-sm text-gray-600 font-medium">Loading…</span>
            </div>
          </div>
        )}
        {isLoading ? (
          <div className="p-12 text-center">
            <Spinner size="lg" />
            <p className="mt-2 text-gray-500">Loading leads...</p>
          </div>
        ) : leads.length === 0 ? (
          <div className="p-12 text-center">
            <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No leads found</h3>
            <p className="mt-1 text-sm text-gray-500">Try adjusting your filters</p>
            {hasPermission('leads', 'create') && (
              <Link to="/leads/new" className="mt-4 btn-primary inline-flex">
                <PlusIcon className="h-4 w-4 mr-1.5" />Add Lead
              </Link>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-900 border-b border-gray-800">
                <tr>
                  <th className="w-8 px-2 text-center">
                    <input
                      type="checkbox"
                      className="rounded border-white/40 accent-white cursor-pointer"
                      checked={leads.length > 0 && selectedLeads.size === leads.length}
                      ref={el => { if (el) el.indeterminate = selectedLeads.size > 0 && selectedLeads.size < leads.length; }}
                      onChange={toggleSelectAll}
                      onClick={e => e.stopPropagation()}
                    />
                  </th>
                  <th className="w-10 px-2" />
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-white uppercase tracking-wide whitespace-nowrap">
                    <span className="inline-flex items-center gap-1">Created on <ChevronUpIcon className="h-3 w-3" /></span>
                  </th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-white uppercase tracking-wide">Name &amp; Number</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-white uppercase tracking-wide">Brand &amp; Model</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-white uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-white uppercase tracking-wide leading-tight">
                    Buying<br />in Days
                  </th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-white uppercase tracking-wide leading-tight">
                    <span className="inline-flex items-center gap-1">
                      <span>Due Date<br />&amp; Overdue</span>
                      <ChevronDownIcon className="h-3 w-3 flex-shrink-0" />
                    </span>
                  </th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-white uppercase tracking-wide">Last comment</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-white uppercase tracking-wide">Assigned to</th>
                  {isAdminOrManager && (
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-white uppercase tracking-wide">Seen</th>
                  )}
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {leads.map((lead) => {
                  const isExpanded = expandedLeadId === lead._id;
                  const urgency    = BUYING_URGENCY[lead.leadInfo?.buyingTime];
                  // The follow-up's day in India. Overdue = due on an earlier
                  // day; one due today is today's task, not overdue — the
                  // same rule as the Overdue tab and Today's Task.
                  const dueDay       = istDay(lead.nextFollowupDate);
                  const overdueDays  = dueDay ? daysBetween(dueDay, todayIST()) : 0;
                  const isOverdue    = overdueDays > 0;

                  return (
                    <React.Fragment key={lead._id}>
                      {/* ── Lead row ── */}
                      <tr
                        ref={isExpanded ? tableRef : null}
                        className={`cursor-pointer transition-colors ${
                          isExpanded ? 'bg-blue-50' : 'hover:bg-gray-50'
                        }`}
                        onClick={() => toggleExpand(lead._id)}
                      >
                        {/* Checkbox */}
                        <td className="px-2 py-3 text-center w-8">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 accent-primary-600 cursor-pointer"
                            checked={selectedLeads.has(lead._id)}
                            onChange={e => toggleSelect(lead._id, e)}
                            onClick={e => e.stopPropagation()}
                          />
                        </td>

                        {/* Expand — red + icon */}
                        <td className="px-2 py-3 text-center w-10">
                          <button
                            onClick={e => { e.stopPropagation(); toggleExpand(lead._id); }}
                            className={`inline-flex items-center justify-center w-7 h-7 rounded transition-colors font-bold text-xl leading-none ${
                              isExpanded ? 'bg-primary-100 text-primary-600' : 'text-red-500 hover:bg-red-50'
                            }`}
                          >
                            {isExpanded ? '−' : '+'}
                          </button>
                        </td>

                        {/* Created on */}
                        <td className="px-3 py-3 text-xs text-gray-600 whitespace-nowrap">
                          {/* The India day it came in — the day Today's / Yesterday's Enquiries file it under */}
                          {formatDay(istDay(lead.leadInfo.enquiryDate), "d MMM ''yy")}
                        </td>

                        {/* Name & Number */}
                        <td className="px-3 py-3">
                          <div className="font-semibold text-[13px] text-gray-900 leading-snug">
                            {lead.customerInfo.name}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">{lead.customerInfo.phone}</div>
                          <LeadQualityTags lead={lead} />
                          <CrmSyncBadge entries={crmStatus[lead._id]} />
                        </td>

                        {/* Brand & Model */}
                        <td className="px-3 py-3 text-[13px] text-gray-700">
                          {[cleanDisplay(lead.leadInfo.brand), cleanDisplay(lead.leadInfo.model)].filter(Boolean).join(' ') || (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>

                        {/* Status — stage + last activity */}
                        <td className="px-3 py-3">
                          {(() => {
                            const cfg = STATUS_CONFIG[lead.status];
                            const label = cfg?.label || lead.status || 'New';
                            const subLabel = lead.convertedSubType
                              ? lead.convertedSubType === 'retail' ? 'Retail' : 'Booked'
                              : null;
                            return (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg?.color || 'bg-blue-100 text-blue-800'}`}>
                                {subLabel ? `${label} (${subLabel})` : label}
                              </span>
                            );
                          })()}
                          {lead.lastActivitySubTypeName && (
                            <div className="text-xs text-gray-400 mt-0.5 leading-snug">
                              {lead.lastActivitySubTypeName}
                            </div>
                          )}
                        </td>

                        {/* Buying in Days */}
                        <td className="px-3 py-3">
                          {urgency && (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${urgency.cls}`}>
                              {urgency.label}
                            </span>
                          )}
                          <div className={`text-xs mt-0.5 leading-snug ${urgency ? 'text-gray-500' : 'text-gray-700'}`}>
                            {buyingTimeLabel(lead.leadInfo)}
                          </div>
                        </td>

                        {/* Due Date & Overdue */}
                        <td className="px-3 py-3">
                          {dueDay ? (
                            <div>
                              <span className={`text-xs font-medium whitespace-nowrap ${isOverdue ? 'text-red-600' : 'text-gray-600'}`}>
                                {formatDay(dueDay, "d MMM ''yy")}
                              </span>
                              {isOverdue ? (
                                <div className="text-xs text-red-500 font-medium mt-0.5">
                                  Overdue ({overdueDays} {overdueDays === 1 ? 'Day' : 'Days'})
                                </div>
                              ) : overdueDays === 0 && (
                                <div className="text-xs text-amber-600 font-medium mt-0.5">Due today</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </td>

                        {/* Last comment */}
                        <td className="px-3 py-3 max-w-[150px]">
                          {lead.lastRemark ? (
                            <span className="text-xs text-gray-600 line-clamp-2" title={lead.lastRemark}>
                              {lead.lastRemark}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </td>

                        {/* Assigned to */}
                        <td className="px-3 py-3 max-w-[120px]">
                          {lead.assignment?.assignedTo ? (
                            <div className="text-xs text-gray-700 truncate" title={`${lead.assignment.assignedTo.firstName} ${lead.assignment.assignedTo.lastName}`}>
                              {lead.assignment.assignedTo.firstName} {lead.assignment.assignedTo.lastName}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">Unassigned</span>
                          )}
                        </td>

                        {/* Seen — admin/manager only */}
                        {isAdminOrManager && (() => {
                          const assignedId = lead.assignment?.assignedTo?._id || lead.assignment?.assignedTo;
                          const viewEntry  = assignedId && lead.viewedBy?.find(v => String(v.userId) === String(assignedId));
                          return (
                            <td className="px-3 py-3">
                              {assignedId ? (
                                viewEntry
                                  ? <div className="inline-flex items-start gap-1 text-xs text-emerald-600 font-medium whitespace-nowrap">
                                      <EyeIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                                      <div>
                                        <div>Seen</div>
                                        <div className="text-gray-400 font-normal">{formatIST(viewEntry.viewedAt, "dd MMM ''yy, hh:mm a")}</div>
                                      </div>
                                    </div>
                                  : <span className="inline-flex items-center gap-1 text-xs text-red-400 font-medium whitespace-nowrap"><EyeSlashIcon className="h-4 w-4" />Not seen</span>
                              ) : <span className="text-xs text-gray-300">—</span>}
                            </td>
                          );
                        })()}
                      </tr>

                      {/* ── Expanded detail panel ── */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={COL_COUNT} className="p-0 bg-gray-50 border-b border-blue-200">
                            <div className="p-4">
                              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

                                {/* ── LEFT 2/3 ── */}
                                <div className="lg:col-span-2 space-y-4">
                                  <SectionCard title="Lead Insights" teal>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 min-w-0">
                                      <InfoCol label="Finance"                   value="No" />
                                      <InfoCol label="Consumer State/City"       value={[lead.customerInfo.state, lead.customerInfo.city].filter(Boolean).join(' / ') || '—'} />
                                      <InfoCol label="Consumer Pincode"          value="—" />
                                      <InfoCol label="Exchange"                  value="No" />
                                      <InfoCol label="Next Activity Schedule Date" value={lead.nextFollowupDate ? formatDay(istDay(lead.nextFollowupDate), "dd MMM ''yy") : null} />
                                      <InfoCol label="First Update Date"         value={formatIST(lead.createdAt, "dd MMM ''yy HH:mm")} />
                                      <InfoCol label="No. Of Followups"          value={activityLoading ? '...' : String(activity.length)} />
                                      <InfoCol label="EnquiryId"                 value={lead._id} />
                                      <InfoCol label="Buying Days"               value={buyingTimeLabel(lead.leadInfo)} />
                                      <InfoCol label="Assigned to"               value={
                                        lead.assignment?.assignedTo
                                          ? `${lead.assignment.assignedTo.firstName} ${lead.assignment.assignedTo.lastName}`
                                          : null
                                      } />
                                    </div>
                                  </SectionCard>

                                  <SectionCard title="Customer Details" teal>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3 min-w-0">
                                      <InfoCol label="Name"         value={lead.customerInfo.name} />
                                      <div>
                                        <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Mobile No</div>
                                        <div className="flex items-center gap-1.5">
                                          <WhatsAppLink number={lead.customerInfo.phone} />
                                          <span className="text-sm text-gray-800">{lead.customerInfo.phone || '—'}</span>
                                        </div>
                                      </div>
                                      <div>
                                        <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Alternate No</div>
                                        <div className="flex items-center gap-1.5">
                                          {lead.customerInfo.alternatePhone && <WhatsAppLink number={lead.customerInfo.alternatePhone} />}
                                          <span className="text-sm text-gray-800">{lead.customerInfo.alternatePhone || '—'}</span>
                                        </div>
                                      </div>
                                      <InfoCol label="Email"        value={lead.customerInfo.email} />
                                      <InfoCol label="State"        value={lead.customerInfo.state} />
                                      <InfoCol label="City"         value={lead.customerInfo.city} />
                                      <InfoCol label="Subcity"      value={lead.customerInfo.subcity} />
                                      <InfoCol label="Pincode"      value={lead.customerInfo.pincode} />
                                      <InfoCol label="Address"      value={lead.customerInfo.address} />
                                    </div>
                                  </SectionCard>

                                  <SectionCard title="Dealer Details">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 min-w-0">
                                      <InfoRow label="Dealer Name" value={lead.dealerInfo?.dealerName} />
                                      <InfoRow label="Mobile"      value={lead.dealerInfo?.dealerNumber} />
                                      <InfoRow label="Email"       value={lead.dealerInfo?.dealerEmail} />
                                      <InfoRow label="City"        value={lead.dealerInfo?.dealerCity} />
                                      <InfoRow label="Status"      value={lead.dealerInfo?.dealershipStatus} />
                                    </div>
                                  </SectionCard>

                                  <SectionCard title="Test Drive Details">
                                    {activityLoading ? <Spinner size="sm" /> : (() => {
                                      const td = activity.find(a =>
                                        ['test_drive_schedule', 'test_drive_completed'].includes(a.subTypeKey)
                                      );
                                      return td ? (
                                        <div className="space-y-1">
                                          <InfoRow label="Type"    value={td.subTypeName} />
                                          <InfoRow label="Date"    value={formatIST(td.customDate || td.createdAt, 'dd MMM yyyy')} />
                                          <InfoRow label="Remarks" value={td.remark} />
                                        </div>
                                      ) : (
                                        <p className="text-xs text-gray-400">No test drive scheduled yet</p>
                                      );
                                    })()}
                                  </SectionCard>


                                  {/* ── Model Summary ── */}
                                  {activityLoading ? (
                                    <div className="text-center py-4"><Spinner size="sm" /></div>
                                  ) : (() => {
                                    const bookAct = activity.find(a => ['booked', 'already_booked'].includes(a.subTypeKey));
                                    return (
                                      <div>
                                        <h4 className="text-base font-semibold text-gray-800 mb-3">
                                          Model Summary (Model Version: {[cleanDisplay(lead.leadInfo.brand), cleanDisplay(lead.leadInfo.model)].filter(Boolean).join(' ') || '—'})
                                        </h4>
                                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                                          <span className="inline-block px-2.5 py-0.5 rounded text-xs font-bold text-white bg-[#f75d34] uppercase tracking-wide mb-3">Booking</span>
                                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                            <InfoCol label="Booking Status"  value={bookAct?.subTypeName} />
                                            <InfoCol label="Booking Amount"  value={bookAct?.metadata?.bookingNumber} />
                                            <InfoCol label="Booking Date"    value={bookAct?.metadata?.bookingDate ? formatIST(bookAct.metadata.bookingDate, 'dd MMM yyyy') : null} />
                                          </div>
                                          <div className="mt-3">
                                            <InfoCol label="Booking Receipt" value={bookAct?.metadata?.bookingName} />
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>

                                {/* ── RIGHT 1/3 ── */}
                                <div>
                                  <LeadActivityPanel
                                    lead={lead}
                                    onDelete={(id) => deleteLeadMutation.mutate(id)}
                                    deleteLoading={deleteLeadMutation.isLoading}
                                  />
                                </div>
                              </div>

                              {/* ── Activity History — full width ── */}
                              <SectionCard title="Activity History" badge={activityLoading ? null : activity.length}>
                                <ActivityHistoryTable activity={activity} activityLoading={activityLoading} />
                              </SectionCard>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Pagination — bottom of table ── */}
        {(pageNum > 1 || totalCount > 0) && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-white">
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">
                Page <span className="font-medium text-gray-700">{pageNum}</span>
                {totalCount != null && <> · <span className="font-medium text-gray-700">{totalCount.toLocaleString()}</span> total</>}
              </span>
              <div className="flex items-center gap-2">
                <label htmlFor="leadsPerPage" className="text-sm text-gray-500">Per page:</label>
                <select
                  id="leadsPerPage"
                  value={leadsPerPage}
                  onChange={e => handleLeadsPerPageChange(Number(e.target.value))}
                  className="text-sm border border-gray-300 rounded-lg px-2 py-1 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-200"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  {/* No "All": thousands of leads reloaded after every update
                      made updates slow — Download has every lead */}
                </select>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={goPrev}
                disabled={pageNum <= 1}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon className="h-4 w-4" /> Prev
              </button>
              <span className="px-3 py-1.5 text-sm font-semibold text-primary-600 bg-primary-50 border border-primary-200 rounded-lg select-none">
                {pageNum}
              </span>
              <button
                onClick={goNext}
                disabled={!pagination.nextCursor && !pagination.hasMore}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Next <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {showBulkUpload && (
        <BulkUploadModal
          onClose={() => setShowBulkUpload(false)}
          onSuccess={() => {
            queryClient.invalidateQueries('leads');
            queryClient.invalidateQueries('leadTabStats');   // the tab counts, with the list
            queryClient.invalidateQueries('leadStats');
          }}
        />
      )}

      {/* ── Bulk Assign Modal ── */}
      {showBulkAssign && (
        <BulkAssignModal
          selectedCount={selectedLeads.size}
          allUsers={allFilterUsers}
          user={user}
          loading={bulkAssignMutation.isLoading}
          selectedLeadBrands={
            // Distinct brands of the currently-selected leads (lowercased)
            Array.from(new Set(
              leads
                .filter(l => selectedLeads.has(l._id))
                .map(l => (l.leadInfo?.brand || '').trim().toLowerCase())
                .filter(Boolean)
            ))
          }
          onClose={() => setShowBulkAssign(false)}
          onAssign={({ assignedToList, remark }) => bulkAssignMutation.mutate({ assignedToList, remark })}
        />
      )}

      {/* ── Bulk Forward Modal ── */}
      {showBulkForward && (
        <BulkForwardModal
          selectedCount={selectedLeads.size}
          allUsers={allFilterUsers}
          user={user}
          loading={bulkForwardMutation.isLoading}
          onClose={() => setShowBulkForward(false)}
          onForward={({ forwardTo, remark }) => bulkForwardMutation.mutate({ forwardTo, remark })}
        />
      )}

      {/* ── Transfer to Workspace Modal (company only) ── */}
      {showTransfer && (
        <TransferWorkspaceModal
          selectedCount={selectedLeads.size}
          loading={transferMutation.isLoading}
          onClose={() => setShowTransfer(false)}
          onTransfer={({ targetTenantId, mode }) => transferMutation.mutate({ targetTenantId, mode })}
        />
      )}

      {/* ── Floating selection action bar ── */}
      {selectedLeads.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 bg-gray-900 text-white rounded-xl shadow-2xl px-5 py-3">
          <span className="text-sm font-medium">{selectedLeads.size} lead{selectedLeads.size > 1 ? 's' : ''} selected</span>
          <div className="w-px h-4 bg-white/20" />
          <button
            onClick={() => setShowBulkAssign(true)}
            className="flex items-center gap-1.5 text-sm font-medium bg-primary-600 hover:bg-primary-500 rounded-lg px-4 py-1.5 transition-colors"
          >
            <UserPlusIcon className="h-4 w-4" />
            Assign
          </button>
          {hasPermission('leads', 'update') && user?.role !== 'sales_executive' && (
            <button
              onClick={() => setShowBulkForward(true)}
              className="flex items-center gap-1.5 text-sm font-medium bg-blue-600 hover:bg-blue-500 rounded-lg px-4 py-1.5 transition-colors"
              title={`Forward ${selectedLeads.size} selected lead(s)`}
            >
              <ArrowRightIcon className="h-4 w-4" />
              Forward
            </button>
          )}
          {user?.isCompany && (
            <button
              onClick={() => setShowTransfer(true)}
              className="flex items-center gap-1.5 text-sm font-medium bg-primary-600 hover:bg-primary-500 rounded-lg px-4 py-1.5 transition-colors"
              title={`Copy/forward ${selectedLeads.size} selected lead(s) to another workspace`}
            >
              <ArrowsRightLeftIcon className="h-4 w-4" />
              To Workspace
            </button>
          )}
          {hasPermission('leads', 'delete') && user?.role !== 'sales_executive' && (
            <button
              onClick={handleBulkDelete}
              disabled={bulkDeleteMutation.isLoading}
              className="flex items-center gap-1.5 text-sm font-medium bg-danger-600 hover:bg-danger-500 rounded-lg px-4 py-1.5 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              title={`Delete ${selectedLeads.size} selected lead(s)`}
            >
              <TrashIcon className="h-4 w-4" />
              {bulkDeleteMutation.isLoading ? 'Deleting…' : 'Delete'}
            </button>
          )}
          <button
            onClick={() => setSelectedLeads(new Set())}
            className="text-white/60 hover:text-white transition-colors"
            title="Clear selection"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
};
